import React, { useState, useEffect, useRef } from 'react';
import Tooltip from './Tooltip';

const TextInput = ({
  label,
  name,
  placeholder,
  className,
  value,
  onChange,
  onBlur,
  showError,
  showSuccess,
  errorMessage,
  tooltip,
  debounceDelay = 0
}) => {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimer = useRef(null);
  const isDebounced = debounceDelay > 0;

  // Sync local value with prop value when it changes externally
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Determine the CSS class based on validation state
  const getInputClass = () => {
    if (showError) return 'confluent-form-input error-field';
    if (showSuccess) return 'confluent-form-input success-field';
    return className || 'confluent-form-input';
  };

  const handleChange = (e) => {
    const newValue = e.target.value;

    if (isDebounced) {
      // Update local state immediately for responsive UI
      setLocalValue(newValue);

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer to update parent state
      debounceTimer.current = setTimeout(() => {
        onChange(e);
      }, debounceDelay);
    } else {
      // No debouncing - update parent immediately
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    if (isDebounced && debounceTimer.current) {
      // If debounced, immediately apply pending changes on blur
      clearTimeout(debounceTimer.current);
      onChange(e);
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="input-wrapper">
      <div className="label-with-tooltip">
        <label htmlFor={name} className="confluent-form-label">{label}</label>
        {tooltip && <Tooltip text={tooltip} />}
      </div>
      <input
        type="text"
        name={name}
        placeholder={placeholder}
        id={name}
        className={getInputClass()}
        value={isDebounced ? localValue : value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {showError && errorMessage && (
        <div className="field-error-message">{errorMessage}</div>
      )}
      {showSuccess && (
        <div className="field-success-message">✓</div>
      )}
    </div>
  );
};

export default TextInput;